import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "../api";

/**
 * Admin → Analytics.
 *
 * iter39 Group C  — first version (all-time only).
 * iter41         — 7d/30d/90d/all toggle.
 * iter42         — custom date range + delta vs prev period + Refresh.
 *                  Backend now Redis-cached (5-min TTL) to limit
 *                  load on the main service.
 */
const WINDOWS = [
  { value: "7d",  label: "7 days"  },
  { value: "30d", label: "30 days" },
  { value: "90d", label: "90 days" },
  { value: "all", label: "All time" },
];

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}
function isoNDaysAgo(n) {
  const d = new Date(); d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
}

export default function Analytics() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [window, setWindow] = useState("all");
  const [customMode, setCustomMode] = useState(false);
  const [since, setSince] = useState(isoNDaysAgo(30));
  const [until, setUntil] = useState(todayIso());
  const [compare, setCompare] = useState(true);
  const [loading, setLoading] = useState(false);
  const [bumpKey, setBumpKey] = useState(0);  // for Refresh

  const fetchData = useCallback(async (force = false) => {
    setLoading(true);
    setErr(null);
    try {
      const params = new URLSearchParams();
      if (customMode) {
        params.set("since", new Date(since).toISOString());
        params.set("until", new Date(until + "T23:59:59").toISOString());
      } else {
        params.set("window", window);
      }
      if (compare) params.set("compare", "true");
      if (force)   params.set("force",   "true");
      const { data } = await api.get(`/admin/analytics/product?${params.toString()}`);
      setData(data);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [window, customMode, since, until, compare]);

  useEffect(() => { fetchData(); /* eslint-disable-line react-hooks/exhaustive-deps */ }, [window, customMode, since, until, compare, bumpKey]);

  if (err) return <div className="text-red-400 text-sm" data-testid="an-error">{err}</div>;
  if (!data) {
    return (
      <div className="text-muted-foreground font-mono text-sm">Loading product analytics…</div>
    );
  }

  const {
    funnel, retention, mix_by_source, mix_by_type, mix_by_language,
    mix_by_attribution, top_by_jobs, top_by_spend, top_by_referrals,
    pro_renewal_rate, time_to_pro_median_days, bonus_utilisation,
    prev,
  } = data;

  // Delta helpers — only when prev block exists
  const prevFunnel = prev?.funnel || null;
  const prevRetention = prev?.retention || null;

  return (
    <div className="space-y-6" data-testid="analytics-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl tracking-tight">Analytics</h1>
          <p className="text-xs text-muted-foreground font-mono mt-1">
            Product health · conversion · retention · cohort mix · leaderboards.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap" data-testid="an-controls">
          <div
            className="inline-flex border border-border bg-background"
            data-testid="an-window-toggle"
          >
            {WINDOWS.map((w) => (
              <button
                key={w.value}
                onClick={() => { setCustomMode(false); setWindow(w.value); }}
                disabled={loading}
                data-testid={`an-window-${w.value}`}
                className={
                  "px-3 py-1.5 text-xs font-mono uppercase tracking-[0.12em] transition-colors " +
                  (!customMode && window === w.value
                    ? "bg-foreground text-background"
                    : "hover:bg-border/40 text-muted-foreground")
                }
              >
                {w.label}
              </button>
            ))}
            <button
              onClick={() => setCustomMode(true)}
              disabled={loading}
              data-testid="an-window-custom"
              className={
                "px-3 py-1.5 text-xs font-mono uppercase tracking-[0.12em] transition-colors " +
                (customMode
                  ? "bg-foreground text-background"
                  : "hover:bg-border/40 text-muted-foreground")
              }
            >
              Custom
            </button>
          </div>

          {customMode && (
            <div className="inline-flex items-center gap-1 text-xs font-mono" data-testid="an-custom-range">
              <input
                type="date"
                value={since}
                max={until}
                onChange={(e) => setSince(e.target.value)}
                className="bg-background border border-border px-2 py-1 text-xs font-mono"
                data-testid="an-since"
              />
              <span className="text-muted-foreground">→</span>
              <input
                type="date"
                value={until}
                min={since}
                max={todayIso()}
                onChange={(e) => setUntil(e.target.value)}
                className="bg-background border border-border px-2 py-1 text-xs font-mono"
                data-testid="an-until"
              />
            </div>
          )}

          <label
            className="inline-flex items-center gap-1.5 text-xs font-mono text-muted-foreground cursor-pointer"
            data-testid="an-compare-toggle"
          >
            <input
              type="checkbox"
              checked={compare}
              onChange={(e) => setCompare(e.target.checked)}
              className="accent-foreground"
            />
            <span className="uppercase tracking-[0.12em]">Δ vs prev</span>
          </label>

          <button
            onClick={() => { setBumpKey((k) => k + 1); fetchData(true); }}
            disabled={loading}
            data-testid="an-refresh"
            title="Skip Redis cache and recompute"
            className="px-3 py-1.5 text-xs font-mono uppercase tracking-[0.12em] border border-border hover:bg-border/40 transition-colors disabled:opacity-50"
          >
            {loading ? "…" : "⟳ Refresh"}
          </button>
        </div>
      </div>

      {/* ── Funnel ────────────────────────────────────────────── */}
      <Panel title="Conversion funnel" testId="an-funnel">
        <FunnelChart stages={funnel} prevStages={prevFunnel} />
      </Panel>

      {/* ── Retention + Health metrics ──────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-px bg-border">
        <Panel title="Retention" testId="an-retention">
          <div className="grid grid-cols-3 gap-3">
            <RetentionCell label="D1"  data={retention.d1}  prev={prevRetention?.d1} />
            <RetentionCell label="D7"  data={retention.d7}  prev={prevRetention?.d7} />
            <RetentionCell label="D30" data={retention.d30} prev={prevRetention?.d30} />
          </div>
          <p className="text-[10px] text-muted-foreground font-mono mt-3">
            % of cohort (joined ≥ N days ago) who had ≥ 1 job in their first N days.
          </p>
        </Panel>
        <Panel title="Product health">
          <div className="grid grid-cols-3 gap-3">
            <KPI
              label="Pro renewal"
              value={`${pro_renewal_rate}%`}
              prevValue={prev?.pro_renewal_rate}
              unit="pp"
              testId="an-renewal"
            />
            <KPI
              label="Time-to-Pro · median"
              value={time_to_pro_median_days != null ? `${time_to_pro_median_days}d` : "—"}
              prevValue={prev?.time_to_pro_median_days}
              unit="d"
              invertColor
              testId="an-ttp"
            />
            <KPI
              label="Bonus utilised"
              value={`${bonus_utilisation}%`}
              prevValue={prev?.bonus_utilisation}
              unit="pp"
              testId="an-bonus"
            />
          </div>
        </Panel>
      </div>

      {/* ── Mix breakdowns ───────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-px bg-border">
        <Panel title="Video sources">
          <BreakdownList items={mix_by_source} />
        </Panel>
        <Panel title="Job types">
          <BreakdownList items={mix_by_type} />
        </Panel>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-px bg-border">
        <Panel title="Languages">
          <BreakdownList items={mix_by_language} />
        </Panel>
        <Panel title="Attribution">
          <BreakdownList items={mix_by_attribution} colorMap={ATTR_COLORS} />
        </Panel>
      </div>

      {/* ── Leaderboards ─────────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-px bg-border">
        <Panel title="Top by jobs">
          <Leaderboard rows={top_by_jobs} unit="" />
        </Panel>
        <Panel title="Top by spend">
          <Leaderboard rows={top_by_spend} unit="$" prefix />
        </Panel>
        <Panel title="Top by referrals">
          <Leaderboard rows={top_by_referrals} unit="" />
        </Panel>
      </div>
    </div>
  );
}

const ATTR_COLORS = {
  direct: "#94a3b8",
  referral: "#fbbf24",
  affiliate: "#22d3ee",
};

// ── Delta utilities ────────────────────────────────────────────
function formatDelta(cur, prev, unit, invertColor = false) {
  // Returns { text, color } or null when prev is missing/equal/non-numeric.
  if (prev == null || cur == null) return null;
  const c = typeof cur === "number" ? cur : parseFloat(cur);
  const p = typeof prev === "number" ? prev : parseFloat(prev);
  if (Number.isNaN(c) || Number.isNaN(p)) return null;
  const diff = +(c - p).toFixed(1);
  if (diff === 0) return { text: `· 0${unit}`, color: "text-muted-foreground" };
  const arrow = diff > 0 ? "↑" : "↓";
  const sign = diff > 0 ? "+" : "";
  const isGood = invertColor ? diff < 0 : diff > 0;
  const color = isGood ? "text-emerald-400" : "text-red-400";
  return { text: `${arrow} ${sign}${diff}${unit}`, color };
}

function DeltaBadge({ cur, prev, unit, invertColor }) {
  const d = formatDelta(cur, prev, unit, invertColor);
  if (!d) return null;
  return (
    <span className={`text-[10px] font-mono ${d.color}`} data-testid="an-delta">
      {d.text}
    </span>
  );
}

function FunnelChart({ stages, prevStages }) {
  // Layout: [label] [bar] [count+Δ] [step%] [top%]
  // Numbers are deliberately OUTSIDE the bar so they stay readable
  // on the stable page background, regardless of bar fill state.
  // Tightened widths on small screens so the row fits an iPhone.
  const top = stages[0]?.count || 1;
  const prevMap = {};
  if (prevStages) prevStages.forEach((s) => { prevMap[s.stage] = s.count; });
  return (
    <div className="space-y-2 overflow-x-auto">
      <div className="flex items-center gap-2 sm:gap-3 text-[10px] font-mono uppercase tracking-[0.12em] text-muted-foreground min-w-[440px]">
        <div className="w-20 sm:w-32" />
        <div className="flex-1" />
        <div className="w-16 sm:w-24 text-right">count · Δ</div>
        <div className="w-10 sm:w-14 text-right" title="vs previous stage">step</div>
        <div className="w-10 sm:w-14 text-right" title="vs top of funnel">of top</div>
      </div>
      {stages.map((s, i) => {
        const pctOfTop = (s.count / top) * 100;
        const prevCount = i > 0 ? stages[i - 1].count || 1 : null;
        const stepPct = prevCount != null ? Math.round((s.count / prevCount) * 100) : null;
        const topPct = Math.round(pctOfTop);
        return (
          <div
            key={s.stage}
            className="flex items-center gap-2 sm:gap-3 min-w-[440px]"
            data-testid={`funnel-${s.stage}`}
          >
            <div className="w-20 sm:w-32 text-[10px] sm:text-xs font-mono uppercase tracking-[0.12em] text-muted-foreground truncate">
              {s.stage.replace(/_/g, " ")}
            </div>
            <div className="flex-1 h-7 bg-border">
              <div
                className="h-full bg-foreground/70 transition-all"
                style={{ width: `${pctOfTop}%` }}
              />
            </div>
            <div className="w-16 sm:w-24 text-right flex items-center justify-end gap-1.5 sm:gap-2 whitespace-nowrap">
              <DeltaBadge cur={s.count} prev={prevMap[s.stage]} unit="" />
              <span className="text-xs sm:text-sm font-mono tabular-nums">{s.count.toLocaleString()}</span>
            </div>
            <div className="w-10 sm:w-14 text-right text-[10px] sm:text-xs font-mono tabular-nums text-muted-foreground">
              {stepPct != null ? `${stepPct}%` : "—"}
            </div>
            <div className="w-10 sm:w-14 text-right text-[10px] sm:text-xs font-mono tabular-nums text-muted-foreground">
              {i === 0 ? "—" : `${topPct}%`}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function RetentionCell({ label, data, prev }) {
  return (
    <div className="text-center">
      <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-muted-foreground">{label}</div>
      <div className="font-display text-2xl mt-1 tabular-nums">{data.pct}%</div>
      <div className="text-[10px] font-mono text-muted-foreground">
        {data.retained}/{data.cohort_size}
      </div>
      <div className="mt-1">
        <DeltaBadge cur={data.pct} prev={prev?.pct} unit="pp" />
      </div>
    </div>
  );
}

function KPI({ label, value, prevValue, unit, invertColor, testId }) {
  // Strip non-numeric chars from value for delta calc (e.g. "22.6%" → 22.6)
  const numericCur = typeof value === "string" ? parseFloat(value.replace(/[^\d.-]/g, "")) : value;
  return (
    <div className="text-center" data-testid={testId}>
      <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-muted-foreground">{label}</div>
      <div className="font-display text-2xl mt-1 tabular-nums">{value}</div>
      <div className="mt-1">
        <DeltaBadge cur={numericCur} prev={prevValue} unit={unit} invertColor={invertColor} />
      </div>
    </div>
  );
}

function Panel({ title, children, testId }) {
  return (
    <div className="border border-border bg-background p-4" data-testid={testId}>
      <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-muted-foreground mb-3">{title}</div>
      {children}
    </div>
  );
}

function BreakdownList({ items, colorMap }) {
  if (!items?.length) return <div className="text-xs text-muted-foreground font-mono">No data.</div>;
  const total = items.reduce((a, b) => a + b.count, 0) || 1;
  const max = Math.max(...items.map((i) => i.count), 1);
  return (
    <ul className="space-y-2">
      {items.map((it) => {
        const pct = Math.round((it.count / total) * 100);
        const widthPct = (it.count / max) * 100;
        const color = colorMap?.[it.label] || "hsl(var(--foreground) / 0.6)";
        return (
          <li key={it.label}>
            <div className="flex items-center justify-between text-xs font-mono">
              <span className="uppercase tracking-[0.12em]">{it.label}</span>
              <span className="text-muted-foreground tabular-nums">
                {it.count.toLocaleString()} · {pct}%
              </span>
            </div>
            <div className="h-1 bg-border mt-1">
              <div className="h-full" style={{ width: `${widthPct}%`, background: color }} />
            </div>
          </li>
        );
      })}
    </ul>
  );
}

function Leaderboard({ rows, unit, prefix }) {
  if (!rows?.length) return <div className="text-xs text-muted-foreground font-mono">No data yet.</div>;
  return (
    <ul className="divide-y divide-border">
      {rows.map((r, i) => (
        <li key={r.id || i} className="py-2 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-[10px] font-mono text-muted-foreground w-4 tabular-nums">#{i + 1}</span>
            {r.id ? (
              <Link to={`/admin/users/${r.id}`} className="text-sm hover:underline truncate">
                {r.first_name || r.username || `tg-${r.telegram_user_id}`}
              </Link>
            ) : (
              <span className="text-sm">—</span>
            )}
            {r.plan === "pro" && (
              <span className="text-[10px] font-mono text-emerald-400 uppercase">pro</span>
            )}
          </div>
          <span className="font-mono text-sm tabular-nums whitespace-nowrap">
            {prefix && unit}{typeof r.value === "number" ? r.value.toLocaleString() : r.value}{!prefix && unit}
          </span>
        </li>
      ))}
    </ul>
  );
}
